<template>
  <aside class="financial-plan-page">
    <DocumentPageTitle
      :word-count="totalWords"
      :word-limit="wordLimit"
      title="Financial Plan"
    />

    <div
      v-for="storeItem in storeItems"
      :id="storeItem.id"
      :key="storeItem.id"
      class="mb-8"
    >
      <template v-if="storeItem.type === 'editor'">
        <TextEditorInput
          :id="`${storeItem.id}-editor`"
          v-model="planContentKey[storeItem.storeKey].text"
          :label="storeItem.label"
          :word-limit="wordLimit"
          :total-word-count="totalWords"
          @update:word-count="handleWordCountUpdate"
        />

        <DocumentAnnexureUploadButton :store-key="`financialPlan.${storeItem.storeKey}.documents`" />
      </template>

      <FinancialPlanBudgetTables v-else-if="storeItem.type === 'budget'" />

      <template v-else-if="storeItem.type === 'table'">
        <h2 class="!mb-1">
          {{ storeItem.label }}
        </h2>

        <!-- Editor-only guidance naming the National Treasury table to source from; excluded from the published PDF -->
        <p
          v-if="storeItem.tableReference"
          class="table-reference"
        >
          {{ storeItem.tableReference }}
        </p>

        <FinancialPlanTable
          :id="storeItem.id"
          :store-key="storeItem.storeKey"
        />
      </template>
    </div>
  </aside>
</template>

<script setup>
import {
  DocumentAnnexureUploadButton,
  DocumentPageTitle,
  FinancialPlanTable,
  FinancialPlanBudgetTables,
} from '@/components/plan-document/components';

import { TextEditorInput } from '@/components/form-inputs';
import { useString, useTinyMCEWordCount } from '@/composables';
import { usePlanStore } from '@/store';
import { computed } from 'vue';

const planStore = usePlanStore();
const string = useString();

const wordLimit = 4800;

const planContentKey = computed(() => {
  return planStore.content?.financialPlan || null;
});

const storeItems = [
  { label: 'Financial strategy', storeKey: 'strategy', type: 'editor' },
  { label: 'Budget per development strategy', type: 'budget' },
  { label: 'Projection of financial performance', tableReference: 'Table A1', storeKey: 'financialPerformanceProjection', type: 'table' },
  { label: 'Capital expenditure and financing', tableReference: 'Table A5', storeKey: 'capitalExpenditureAndFinancing', type: 'table' },
  { label: 'Projection of financial position', tableReference: 'Table A6', storeKey: 'financialPositionProjection', type: 'table' },
  { label: 'Projection of cash flow', tableReference: 'Table A7', storeKey: 'cashFlowProjection', type: 'table' },
  { label: 'Comments to the financial plan', storeKey: 'comments', type: 'editor' },
].map(storeItem => ({ ...storeItem, id: string.sluggify(storeItem.label) }));

const editorStoreItemIds = storeItems
  .filter(s => s.type === 'editor')
  .map(s => `${s.id}-editor`);

const {
  totalWords,
  updateCount,
} = useTinyMCEWordCount(wordLimit, editorStoreItemIds);

const handleWordCountUpdate = ({ id, count }) => {
  updateCount(id, count);
};
</script>
